import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, Car } from 'lucide-react';

const NAV = [
  { label: 'Hem', to: '/' },
  { label: 'Körlektioner', to: '/korlektioner' },
  {
    label: 'Riskutbildning',
    children: [
      { label: 'Riskutbildning Del 1 – Teori', desc: '499 kr · 3 timmar', to: '/riskutbildning-1' },
      { label: 'Riskutbildning Del 2 – Halkbana', desc: '1 990 kr · Heldag', to: '/riskutbildning-2' },
    ],
  },
  { label: 'Teorilektioner', to: '/teorilektioner' },
  { label: 'Om Oss', to: '/om-oss' },
  { label: 'Kontakta Oss', to: '/kontakt' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); setOpenDrop(null); }, [location]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 36);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <header className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${scrolled ? 'shadow-md' : ''}`}>
      {/* Top bar */}
      <div className="bg-gray-50 border-b border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-9 text-xs text-gray-500">
          <div className="flex items-center gap-5">
            <a href="tel:+46737547405" className="flex items-center gap-1.5 hover:text-red-600 transition-colors font-medium">
              <Phone size={11}/> Abubakar: 073-754 74 05
            </a>
            <a href="tel:+46704815743" className="flex items-center gap-1.5 hover:text-red-600 transition-colors font-medium">
              <Phone size={11}/> Ahmed: 070-481 57 43
            </a>
            <a href="mailto:info@alfatrafikskola.se" className="flex items-center gap-1.5 hover:text-red-600 transition-colors">
              <Mail size={11}/> info@alfatrafikskola.se
            </a>
          </div>
          <span className="text-gray-400">Nordkapsgatan 8, Kista · Mån–Fre 08–18 · Lör 09–14</span>
        </div>
      </div>

      {/* Main nav */}
      <div className={`border-b-2 border-red-600 bg-white`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-6 h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center group-hover:bg-red-700 transition-colors">
              <Car size={19} className="text-white"/>
            </div>
            <div>
              <div className="font-display font-black text-2xl text-red-600 leading-none tracking-wide">ALFA</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400 leading-none">Trafikskola</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
            {NAV.map(item =>
              item.children ? (
                <div key={item.label} className="relative"
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}>
                  <button className={`flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg transition-colors uppercase tracking-wide ${openDrop === item.label ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'}`}>
                    {item.label}
                    <ChevronDown size={12} className={`transition-transform duration-200 ${openDrop === item.label ? 'rotate-180' : ''}`}/>
                  </button>
                  <div className={`absolute top-full left-0 pt-1 transition-all duration-150 ${openDrop === item.label ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'}`}>
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[280px] border-t-2 border-t-red-600">
                      {item.children.map(c => (
                        <Link key={c.to} to={c.to} className="flex flex-col px-4 py-3 hover:bg-red-50 transition-colors group/item">
                          <span className="text-sm font-semibold text-gray-800 group-hover/item:text-red-600">{c.label}</span>
                          {c.desc && <span className="text-xs text-gray-400 mt-0.5">{c.desc}</span>}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={item.to} to={item.to}
                  className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors uppercase tracking-wide ${location.pathname === item.to ? 'text-red-600 bg-red-50' : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'}`}>
                  {item.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <Link to="/korlektioner" className="hidden lg:inline-flex btn-red">
            Boka Lektion
          </Link>

          {/* Mobile toggle */}
          <button className="lg:hidden ml-auto p-2 text-gray-600 hover:text-red-600 transition-colors" onClick={() => setMobileOpen(o => !o)}>
            {mobileOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 bg-white z-40 transition-transform duration-300 overflow-y-auto pt-[107px] lg:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 flex flex-col gap-1">
          {/* Contact */}
          <div className="bg-red-50 rounded-xl p-4 mb-4 flex flex-col gap-2">
            <a href="tel:+46737547405" className="flex items-center gap-2 text-sm text-red-700 font-semibold"><Phone size={13}/> Abubakar: 073-754 74 05</a>
            <a href="tel:+46704815743" className="flex items-center gap-2 text-sm text-red-700 font-semibold"><Phone size={13}/> Ahmed: 070-481 57 43</a>
            <a href="mailto:info@alfatrafikskola.se" className="flex items-center gap-2 text-sm text-red-700 font-semibold"><Mail size={13}/> info@alfatrafikskola.se</a>
          </div>
          {NAV.map(item =>
            item.children ? (
              <div key={item.label} className="mb-2">
                <div className="text-[11px] font-black uppercase tracking-widest text-red-500 px-2 pb-2 border-b border-red-100 mb-1">{item.label}</div>
                {item.children.map(c => (
                  <Link key={c.to} to={c.to} className="flex items-center justify-between px-3 py-3 rounded-lg hover:bg-red-50 text-gray-700 hover:text-red-600 transition-colors">
                    <span className="text-sm font-semibold">{c.label}</span>
                    {c.desc && <span className="text-xs text-gray-400">{c.desc}</span>}
                  </Link>
                ))}
              </div>
            ) : (
              <Link key={item.to} to={item.to} className={`px-3 py-3 rounded-lg text-sm font-semibold uppercase tracking-wide transition-colors ${location.pathname === item.to ? 'bg-red-50 text-red-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                {item.label}
              </Link>
            )
          )}
          <Link to="/korlektioner" className="btn-red mt-4 justify-center">Boka Körlektion Nu</Link>
        </div>
      </div>
    </header>
  );
}
